import type { SpanData } from './tel-types'

type Keys = { traceId?: string; requestId?: string; parentId?: string; finished: boolean }

/** A span is finished once it has a final status and a duration (the rule every boundary check used). */
export function isFinishedSpan(span: SpanData): boolean {
  return (span.status === 'ok' || span.status === 'error' || span.status === 'warning') && typeof span.duration === 'number'
}

/**
 * The in-memory span store, indexed by span id, trace id, request id and parent id, oldest first.
 * Every lookup, boundary check and eviction costs the same at 10 spans as at the 5,000-span cap: it used to be an
 * array scanned per inserted or ended span (up to 5,000 comparisons each, ~11 ms of main thread per page commit on the
 * category bench, 2026-10-30). Prior art: Jaeger's in-memory store (`plugin/storage/memory/memory.go`:
 * spans indexed by trace id, oldest-first eviction over insertion order). `visits` counts the stored spans each operation looks at, for the constant-cost test.
 */
export class SpanStore {
  readonly #byId = new Map<string, SpanData>()
  readonly #keys = new Map<string, Keys>()
  readonly #byTrace = new Map<string, Map<string, SpanData>>()
  readonly #byRequest = new Map<string, Map<string, SpanData>>()
  readonly #byParent = new Map<string, Map<string, SpanData>>()
  readonly #unfinishedByTrace = new Map<string, number>()
  readonly #unfinishedByRequest = new Map<string, number>()
  visits = 0

  get size(): number { return this.#byId.size }

  get(id: string): SpanData | undefined { this.visits++; return this.#byId.get(id) }

  has(id: string): boolean { this.visits++; return this.#byId.has(id) }

  /** A whole-store walk: it counts every stored span, so a hot path that scans shows up in `visits`. */
  values(): IterableIterator<SpanData> { this.visits += this.#byId.size; return this.#byId.values() }

  toArray(): SpanData[] { this.visits += this.#byId.size; return [...this.#byId.values()] }

  add(span: SpanData): void {
    if (this.#byId.has(span.id)) this.delete(span.id)
    this.#byId.set(span.id, span)
    this.#index(span)
  }

  /** Re-index a stored span after its ids, status or duration changed (`update`, `end`, a relayed patch). Only the
   * changed keys move: a status or duration change keeps the span's place among its parent's children (the order traces
   * are rebuilt in); a parent change moves it to the end of its new parent's children. */
  touch(span: SpanData): void {
    const keys = this.#keys.get(span.id)
    if (!keys) return
    const finished = isFinishedSpan(span)
    if (!keys.finished) this.#countUnfinished(keys, -1)
    if (keys.traceId !== span.traceId) {
      if (keys.traceId) this.#leave(this.#byTrace, keys.traceId, span.id)
      if (span.traceId) this.#join(this.#byTrace, span.traceId, span)
      keys.traceId = span.traceId
    }
    if (keys.requestId !== span.requestId) {
      if (keys.requestId) this.#leave(this.#byRequest, keys.requestId, span.id)
      if (span.requestId) this.#join(this.#byRequest, span.requestId, span)
      keys.requestId = span.requestId
    }
    if (keys.parentId !== span.parentId) {
      if (keys.parentId) this.#leave(this.#byParent, keys.parentId, span.id)
      if (span.parentId) this.#join(this.#byParent, span.parentId, span)
      keys.parentId = span.parentId
    }
    keys.finished = finished
    if (!finished) this.#countUnfinished(keys, 1)
  }

  delete(id: string): SpanData | undefined {
    const span = this.#byId.get(id)
    if (!span) return undefined
    this.#byId.delete(id)
    const keys = this.#keys.get(id)
    if (keys) this.#unindex(id, keys)
    return span
  }

  inTrace(traceId: string): SpanData[] { return this.#group(this.#byTrace, traceId) }

  inRequest(requestId: string): SpanData[] { return this.#group(this.#byRequest, requestId) }

  children(parentId: string): SpanData[] { return this.#group(this.#byParent, parentId) }

  hasUnfinished(field: 'traceId' | 'requestId', id: string): boolean {
    this.visits++
    return ((field === 'traceId' ? this.#unfinishedByTrace : this.#unfinishedByRequest).get(id) ?? 0) > 0
  }

  /**
   * Removes the oldest span the first matching rule allows, rules tried in order. It walks from the oldest span and
   * stops at the first match, so it looks only at the spans the earlier rules protect (open and pinned spans), not
   * at the whole store.
   */
  evictOldest(rules: ((span: SpanData) => boolean)[]): SpanData | undefined {
    for (const allowed of rules) {
      for (const span of this.#byId.values()) {
        this.visits++
        if (allowed(span)) return this.delete(span.id)
      }
    }
    const oldest = this.#byId.values().next()
    return oldest.done ? undefined : this.delete(oldest.value.id)
  }

  clear(): void {
    for (const map of [this.#byId, this.#keys, this.#byTrace, this.#byRequest, this.#byParent, this.#unfinishedByTrace, this.#unfinishedByRequest]) map.clear()
  }

  #group(map: Map<string, Map<string, SpanData>>, key: string): SpanData[] {
    const group = map.get(key)
    if (!group) return []
    this.visits += group.size
    return [...group.values()]
  }

  #index(span: SpanData): void {
    const keys: Keys = { traceId: span.traceId, requestId: span.requestId, parentId: span.parentId, finished: isFinishedSpan(span) }
    this.#keys.set(span.id, keys)
    if (keys.traceId) this.#join(this.#byTrace, keys.traceId, span)
    if (keys.requestId) this.#join(this.#byRequest, keys.requestId, span)
    if (keys.parentId) this.#join(this.#byParent, keys.parentId, span)
    if (!keys.finished) this.#countUnfinished(keys, 1)
  }

  #unindex(id: string, keys: Keys): void {
    this.#keys.delete(id)
    if (keys.traceId) this.#leave(this.#byTrace, keys.traceId, id)
    if (keys.requestId) this.#leave(this.#byRequest, keys.requestId, id)
    if (keys.parentId) this.#leave(this.#byParent, keys.parentId, id)
    if (!keys.finished) this.#countUnfinished(keys, -1)
  }

  #countUnfinished(keys: Keys, by: 1 | -1): void {
    if (keys.traceId) _count(this.#unfinishedByTrace, keys.traceId, by)
    if (keys.requestId) _count(this.#unfinishedByRequest, keys.requestId, by)
  }

  #join(map: Map<string, Map<string, SpanData>>, key: string, span: SpanData): void {
    let group = map.get(key)
    if (!group) { group = new Map(); map.set(key, group) }
    group.set(span.id, span)
  }

  #leave(map: Map<string, Map<string, SpanData>>, key: string, id: string): void {
    const group = map.get(key)
    if (!group) return
    group.delete(id)
    if (group.size === 0) map.delete(key)
  }
}

function _count(map: Map<string, number>, key: string, by: 1 | -1): void {
  const next = (map.get(key) ?? 0) + by
  if (next > 0) map.set(key, next)
  else map.delete(key)
}
